import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaCloudUploadAlt,
  FaImage,
  FaKey,
  FaSave,
  FaSpinner,
} from "react-icons/fa";

import useAuth from "../../hooks/useAuthContext";
import useCadastro from "../../hooks/useCadastroContext";
import useApp from "../../hooks/useAppContext";
import InputTextPatrocinador from "../cadastro/inputtextPatrocinador";
import InputRadio from "../cadastro/inputradio";
import { cropAvatarCenterSquare } from "../../utils/cropAvatarImage";
import {
  ESTADOS_BR,
  REGIOES_BR,
  regiaoPorEstado,
} from "../../constants/estadosRegioes";
import api from "../../services/axios";

import "../cadastro/cadastro.css";
import "../adm/admPatrocinadoresList.css";
import "../adm/admFormPatrocinadores.css";
import "../adm/admFormConvidados.css";
import "./perfil.css";

const SEGMENTOS = [
  ["", "Selecione uma opção"],
  ["clinica", "Clínica"],
  ["farmaceutica", "Farmacêutica"],
  ["homecare", "Home care"],
  ["hospital", "Hospital"],
  ["laboratorio", "Laboratório"],
  ["operadora", "Operadora"],
  ["outros", "Outros"],
];

const PROCURA = [
  ["", "Selecione uma opção"],
  ["consultoria", "Consultoria"],
  ["contrucao_civil", "Construção civil"],
  ["consumiveis", "Consumíveis"],
  ["equipamentos", "Equipamentos"],
  ["facilities", "Facilities"],
  ["homecare", "Home care"],
  ["servicos", "Serviços"],
  ["tecnologia", "Tecnologia"],
  ["outros", "Outros"],
];

const FATURAMENTO = [
  ["", "Selecione uma opção"],
  ["Abaixo de 1M", "Abaixo de 1M"],
  ["Entre 1M e 300M", "Entre 1M e 300M"],
  ["Entre 300M e 500M", "Entre 300M e 500M"],
  ["Entre 500M e 1B", "Entre 500M e 1B"],
  ["Entre 1B e 5B", "Entre 1B e 5B"],
  ["Superior a 5B", "Superior a 5B"],
];

const ATIVIDADES = [
  ["", "Selecione uma opção"],
  ["Gastronomia", "Gastronomia"],
  ["Esportes", "Esportes"],
  ["Cinema", "Cinema"],
  ["Viagens", "Viagens"],
  ["Leitura", "Leitura"],
  ["outros", "Outros"],
];

function parseHobbyForm(hobbySalvo) {
  const raw = String(hobbySalvo || "").trim();
  if (!raw) return { atividade: "", outros: "" };
  const outrosMatch = /^Outros:\s*(.*)$/i.exec(raw);
  if (outrosMatch) {
    return { atividade: "outros", outros: outrosMatch[1].trim() };
  }
  const conhecidas = ATIVIDADES.map(([v]) => v).filter(Boolean);
  if (conhecidas.includes(raw)) {
    return { atividade: raw, outros: "" };
  }
  return { atividade: "outros", outros: raw };
}

function formatHobbyForm(atividade, outros) {
  if (!atividade) return "";
  if (atividade === "outros") {
    const texto = String(outros || "").trim();
    return texto ? `Outros: ${texto}` : "";
  }
  return atividade;
}

function telefoneComercialParaInput(dddComercial, telefoneComercial) {
  const ddd = String(dddComercial || "").replace(/\D/g, "");
  const tel = String(telefoneComercial || "").replace(/\D/g, "");
  if (!ddd && !tel) return "";
  if (!ddd) return tel;
  if (!tel) return ddd;
  if (tel.length >= 10) return tel.startsWith(ddd) ? tel : ddd + tel;
  if (tel.startsWith(ddd)) return tel;
  return ddd + tel;
}

function normalizarTexto(valor) {
  return String(valor ?? "").trim();
}

function normalizarCelular(valor) {
  let digits = String(valor || "").replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length >= 12) {
    digits = digits.slice(2);
  }
  return digits;
}

function splitTelefoneComercial(valor) {
  const telDigits = String(valor || "").replace(/\D/g, "");
  const dddSalvar =
    telDigits.length >= 10
      ? telDigits.slice(0, 2)
      : telDigits.length >= 2
        ? telDigits.slice(0, 2)
        : "";
  const telSalvar =
    telDigits.length >= 10
      ? telDigits.slice(2)
      : telDigits.slice(dddSalvar.length);
  return { ddd: dddSalvar, telefone: telSalvar };
}

const TelaPerfil = () => {
  const navigate = useNavigate();
  const { usuario, setIsAppLoaded } = useAuth();
  const { handleImagemParticipante } = useApp();
  const {
    nomeCompleto,
    setNomeCompleto,
    nomeCracha,
    setNomeCracha,
    email,
    setEmail,
    cpf,
    setCPF,
    dataNascimento,
    setDataNascimento,
    sexo,
    setSexo,
    telComercial,
    setTelComercial,
    setDdd,
    celular,
    setCelular,
    nomeInstituicao,
    setnomeInstituicao,
    cargo,
    setCargo,
    segmento,
    setSegmento,
    segmentoOutros,
    setSegmentoOutros,
    bio,
    setBio,
    estado,
    setEstado,
    regiao,
    setRegiao,
    faturamento,
    setfaturamento,
    procura,
    setprocura,
    outrosProcura,
    setOutrosProcura,
    hobby,
    setHobby,
    prioridade,
    setPrioridade,
    transformacao,
    setTransformacao,
    setTipo,
  } = useCadastro();

  const isPatrocinador = usuario?.tipo === "patrocinador";
  const isConvidado = usuario?.tipo === "convidado";
  const isParticipantePatrocinador =
    usuario?.tipo === "participante_patrocinador";
  const fileInputRef = useRef(null);
  const previewBlobRef = useRef(null);
  const snapshotRef = useRef(null);
  const avatarFileRef = useRef(null);
  const loadedUserIdRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [avatarDragActive, setAvatarDragActive] = useState(false);
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hobbyAtividade, setHobbyAtividade] = useState("");
  const [hobbyOutros, setHobbyOutros] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmaSenha, setConfirmaSenha] = useState("");
  const [isAlterandoSenha, setIsAlterandoSenha] = useState(false);

  const avatarExibir = preview || handleImagemParticipante(usuario);

  const atualizarHobby = useCallback(
    (atividade, outros) => {
      setHobbyAtividade(atividade);
      setHobbyOutros(outros);
      setHobby(formatHobbyForm(atividade, outros));
    },
    [setHobby],
  );

  const carregaUsuario = useCallback(() => {
    if (!usuario?.id) return;

    // Evita sobrescrever edições locais quando o objeto usuario só muda de referência
    if (loadedUserIdRef.current === usuario.id && snapshotRef.current) {
      return;
    }

    const dddComercial = String(usuario.ddd_comercial || "").replace(/\D/g, "");
    const telefoneCompleto = telefoneComercialParaInput(
      dddComercial,
      usuario.telefone_comercial,
    );
    const celularNorm = normalizarCelular(usuario.celular);
    const sexoLabel =
      usuario?.sexo === "feminino"
        ? "Feminino"
        : usuario?.sexo === "masculino"
          ? "Masculino"
          : "";
    const hobbyParsed = parseHobbyForm(usuario.hobby);

    loadedUserIdRef.current = usuario.id;

    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    setPreview(null);
    avatarFileRef.current = null;

    setNomeCompleto(usuario.nome || "");
    setNomeCracha(usuario.nome_cracha || "");
    setEmail(usuario.email || "");
    setCPF(usuario.cpf || "");
    setDataNascimento(usuario?.dt_nascimento?.substring(0, 10) || "");
    setSexo(sexoLabel);
    setDdd(dddComercial || telefoneCompleto.slice(0, 2));
    setTelComercial(telefoneCompleto);
    setCelular(celularNorm);
    setnomeInstituicao(usuario.empresa || "");
    setCargo(usuario.cargo || "");
    setEstado(usuario.estado || "");
    setRegiao(usuario.regiao || "");
    setSegmento(usuario.tipo_instituicao || "");
    setSegmentoOutros(
      usuario.tipo_instituicao === "outros"
        ? usuario.segmento_outros || ""
        : "",
    );
    setBio(usuario.bio || "");
    setfaturamento(usuario.faturamento_instituicao || "");
    setprocura(usuario.procura_sbf || "");
    setOutrosProcura(usuario.outros_procura_sbf || "");
    setPrioridade(usuario.prioridade || "");
    setTransformacao(usuario.transformacao || "");
    setHobbyAtividade(hobbyParsed.atividade);
    setHobbyOutros(hobbyParsed.outros);
    setHobby(usuario.hobby || "");
    setTipo(usuario.tipo || "");

    snapshotRef.current = {
      nome: normalizarTexto(usuario.nome),
      nome_cracha: normalizarTexto(usuario.nome_cracha),
      cpf: normalizarTexto(usuario.cpf),
      dt_nascimento: usuario?.dt_nascimento?.substring(0, 10) || "",
      sexo: sexoLabel,
      telComercial: normalizarCelular(telefoneCompleto),
      celular: celularNorm,
      empresa: normalizarTexto(usuario.empresa),
      cargo: normalizarTexto(usuario.cargo),
      estado: normalizarTexto(usuario.estado),
      regiao: normalizarTexto(usuario.regiao),
      bio: normalizarTexto(usuario.bio),
      tipo_instituicao: normalizarTexto(usuario.tipo_instituicao),
      segmento_outros: normalizarTexto(usuario.segmento_outros),
      faturamento_instituicao: normalizarTexto(usuario.faturamento_instituicao),
      procura_sbf: normalizarTexto(usuario.procura_sbf),
      outros_procura_sbf: normalizarTexto(usuario.outros_procura_sbf),
      prioridade: normalizarTexto(usuario.prioridade),
      transformacao: normalizarTexto(usuario.transformacao),
      hobby: normalizarTexto(usuario.hobby),
    };
  }, [
    usuario,
    setNomeCompleto,
    setNomeCracha,
    setEmail,
    setCPF,
    setDataNascimento,
    setSexo,
    setDdd,
    setTelComercial,
    setCelular,
    setnomeInstituicao,
    setCargo,
    setEstado,
    setRegiao,
    setSegmento,
    setSegmentoOutros,
    setBio,
    setfaturamento,
    setprocura,
    setOutrosProcura,
    setPrioridade,
    setTransformacao,
    setHobby,
    setTipo,
  ]);

  useEffect(() => {
    carregaUsuario();
  }, [carregaUsuario]);

  useEffect(() => {
    return () => {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, []);

  const applyAvatarFile = useCallback(
    async (file) => {
      if (!file || isPatrocinador) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Use um arquivo de imagem (PNG, JPG, WebP, etc.).");
        return;
      }
      const maxBytes = 15 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error("Imagem muito grande. Tamanho máximo: 15 MB.");
        return;
      }
      setAvatarProcessing(true);
      try {
        const cropped = await cropAvatarCenterSquare(file);
        if (previewBlobRef.current) {
          URL.revokeObjectURL(previewBlobRef.current);
        }
        const url = URL.createObjectURL(cropped);
        previewBlobRef.current = url;
        avatarFileRef.current = cropped;
        setPreview(url);
      } catch (err) {
        console.error(err);
        toast.error("Não foi possível processar a imagem.");
      } finally {
        setAvatarProcessing(false);
      }
    },
    [isPatrocinador],
  );

  const handleAvatarInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) applyAvatarFile(f);
    e.target.value = "";
  };

  const openAvatarPicker = () => {
    if (!isPatrocinador && !avatarProcessing) {
      fileInputRef.current?.click();
    }
  };

  const onAvatarDragEnter = (e) => {
    if (isPatrocinador) return;
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragActive(true);
  };

  const onAvatarDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setAvatarDragActive(false);
    }
  };

  const onAvatarDragOver = (e) => {
    if (isPatrocinador) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onAvatarDrop = (e) => {
    if (isPatrocinador) return;
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) applyAvatarFile(f);
  };

  const onAvatarZoneKeyDown = (e) => {
    if (isPatrocinador) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAvatarPicker();
    }
  };

  const montarFormDataParcial = () => {
    const snap = snapshotRef.current || {};
    const formData = new FormData();
    let alteracoes = 0;

    const appendSeMudou = (campoApi, valorAtual, valorOriginal) => {
      const atual = normalizarTexto(valorAtual);
      const original = normalizarTexto(valorOriginal);
      if (atual !== original) {
        formData.append(campoApi, atual);
        alteracoes += 1;
      }
    };

    if (isPatrocinador) {
      const celAtual = normalizarCelular(celular);
      if (celAtual !== normalizarTexto(snap.celular)) {
        formData.append("celular", celAtual);
        alteracoes += 1;
      }
      return { formData, alteracoes };
    }

    appendSeMudou("nome", nomeCompleto, snap.nome);
    appendSeMudou("nome_cracha", nomeCracha, snap.nome_cracha);
    appendSeMudou("cpf", cpf, snap.cpf);
    appendSeMudou("dt_nascimento", dataNascimento, snap.dt_nascimento);
    appendSeMudou("sexo", String(sexo || "").toLowerCase(), String(snap.sexo || "").toLowerCase());
    appendSeMudou("bio", bio, snap.bio);
    if (!isParticipantePatrocinador) {
      appendSeMudou("empresa", nomeInstituicao, snap.empresa);
    }
    appendSeMudou("cargo", cargo, snap.cargo);

    if (isConvidado) {
      appendSeMudou("tipo_instituicao", segmento, snap.tipo_instituicao);
      appendSeMudou(
        "segmento_outros",
        segmento === "outros" ? segmentoOutros : "",
        snap.segmento_outros,
      );
      appendSeMudou("faturamento_instituicao", faturamento, snap.faturamento_instituicao);
      appendSeMudou("procura_sbf", procura, snap.procura_sbf);
      appendSeMudou(
        "outros_procura_sbf",
        procura === "outros" ? outrosProcura : "",
        snap.outros_procura_sbf,
      );
      appendSeMudou("prioridade", prioridade, snap.prioridade);
      appendSeMudou("transformacao", transformacao, snap.transformacao);
      appendSeMudou("hobby", hobby, snap.hobby);
    }

    const celAtual = normalizarCelular(celular);
    if (celAtual !== normalizarTexto(snap.celular)) {
      formData.append("celular", celAtual);
      alteracoes += 1;
    }

    const telAtual = normalizarCelular(telComercial);
    if (telAtual !== normalizarTexto(snap.telComercial)) {
      const { ddd, telefone } = splitTelefoneComercial(telComercial);
      formData.append("ddd_comercial", ddd);
      formData.append("telefone_comercial", telefone);
      alteracoes += 1;
    }

    const estadoAtual = normalizarTexto(estado);
    const regiaoAtual = normalizarTexto(regiao);
    if (
      estadoAtual !== normalizarTexto(snap.estado) ||
      regiaoAtual !== normalizarTexto(snap.regiao)
    ) {
      formData.append("estado", estadoAtual);
      formData.append("regiao", regiaoAtual);
      alteracoes += 1;
    }

    if (avatarFileRef.current) {
      formData.append("avatar", avatarFileRef.current);
      alteracoes += 1;
    }

    return { formData, alteracoes };
  };

  const handleSalvar = async () => {
    const { formData, alteracoes } = montarFormDataParcial();
    if (!alteracoes) {
      toast.info("Nenhuma alteração para salvar.");
      return;
    }

    setIsSaving(true);
    setIsAppLoaded(false);
    try {
      const resp = await api.put(`/usuarios-update/${usuario.id}`, formData, {
        headers: { "content-type": "multipart/form-data" },
      });
      if (resp.status === 200) {
        toast.success("Dados atualizados com sucesso!", { theme: "colored" });
        navigate(0);
      } else {
        toast.error("Não foi possível salvar os dados.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Não foi possível salvar os dados.",
      );
    } finally {
      setIsAppLoaded(true);
      setIsSaving(false);
    }
  };

  const handleAlterarSenha = async () => {
    if (!senhaAtual || !novaSenha || !confirmaSenha) {
      toast.error("Preencha todos os campos de senha.");
      return;
    }
    if (novaSenha.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (novaSenha !== confirmaSenha) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }

    setIsAlterandoSenha(true);
    try {
      const resp = await api.post("/alterar-senha-perfil", {
        senha_atual: senhaAtual,
        nova_senha: novaSenha,
        confirma_senha: confirmaSenha,
      });
      if (resp.status === 200) {
        toast.success(resp.data?.mensagem || "Senha alterada com sucesso!", {
          theme: "colored",
        });
        setSenhaAtual("");
        setNovaSenha("");
        setConfirmaSenha("");
      } else {
        toast.error("Não foi possível alterar a senha.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Não foi possível alterar a senha.",
      );
    } finally {
      setIsAlterandoSenha(false);
    }
  };

  const formActions = (
    <div className="apf-form-actions perfil-form-actions--save-only">
      <button
        type="button"
        className="apl-btn apl-btn--primary"
        onClick={handleSalvar}
        disabled={isSaving || avatarProcessing}
      >
        {isSaving ? (
          <FaSpinner className="fa-spin" size={14} aria-hidden />
        ) : (
          <FaSave size={14} aria-hidden />
        )}
        {isSaving ? "Salvando…" : "Salvar"}
      </button>
    </div>
  );

  return (
    <div className="corpo-pagina perfil-page">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header" aria-label="Cabeçalho do perfil">
          <div className="apl-header-text">
            <h1>Meu perfil</h1>
            <p>
              {isConvidado
                ? "Atualize seus dados cadastrais e as informações de credenciamento."
                : "Atualize seus dados cadastrais."}
              {usuario?.pin ? (
                <span className="perfil-pin-meta"> PIN: {usuario.pin}</span>
              ) : null}
            </p>
          </div>
        </header>

        <section
          className="apl-card apf-form-card"
          aria-label="Formulário de perfil"
        >
          {formActions}

          <h2 className="apf-section-title">Identidade e contato</h2>

          <div className="apf-form-layout">
            <div className="apf-logo-card">
              <p className="apf-logo-label">Foto de perfil</p>
              <div
                className={`apf-upload-zone ${
                  avatarDragActive ? "apf-upload-zone--active" : ""
                } ${avatarProcessing ? "apf-upload-zone--busy" : ""} ${
                  isPatrocinador ? "apf-upload-zone--readonly" : ""
                }`}
                onDragEnter={onAvatarDragEnter}
                onDragLeave={onAvatarDragLeave}
                onDragOver={onAvatarDragOver}
                onDrop={onAvatarDrop}
                onClick={
                  isPatrocinador || avatarProcessing
                    ? undefined
                    : openAvatarPicker
                }
                onKeyDown={onAvatarZoneKeyDown}
                role={isPatrocinador ? "img" : "button"}
                tabIndex={isPatrocinador ? -1 : 0}
                aria-label={
                  isPatrocinador ? "Foto de perfil" : "Enviar foto de perfil"
                }
              >
                {!isPatrocinador ? (
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="visually-hidden"
                    accept="image/*"
                    onChange={handleAvatarInputChange}
                    tabIndex={-1}
                  />
                ) : null}

                {avatarExibir ? (
                  <div className="apf-upload-preview apf-upload-preview--avatar">
                    <img
                      key={preview || `avatar-${usuario?.id || "padrao"}`}
                      src={avatarExibir}
                      alt="Prévia da foto de perfil"
                      className="apf-upload-preview-img apf-avatar-preview-round"
                    />
                    {!isPatrocinador ? (
                      <div className="apf-upload-preview-overlay">
                        {avatarProcessing ? (
                          <>
                            <FaSpinner
                              className="fa-spin"
                              size={20}
                              aria-hidden
                            />
                            <span>Processando…</span>
                          </>
                        ) : (
                          <>
                            <FaImage size={20} aria-hidden />
                            <span>Substituir imagem</span>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="apf-upload-empty">
                    <span className="apf-upload-icon-wrap" aria-hidden>
                      <FaCloudUploadAlt size={36} />
                    </span>
                    <p className="apf-upload-title">
                      Arraste o arquivo ou clique para explorar
                    </p>
                    <p className="apf-upload-sub">
                      PNG, JPG ou WebP · até 15 MB
                    </p>
                    <span className="apf-upload-browse">Escolher arquivo</span>
                  </div>
                )}
              </div>
              <p className="apf-logo-hint">
                {isPatrocinador
                  ? "A foto do patrocinador é gerenciada pela organização."
                  : "Opcional. Recorte central quadrado automático, sem distorcer a proporção. Exibida no perfil e nas listagens."}
              </p>
            </div>

            <div className="apf-controls w-100">
              <div className="row">
                <div className="col-lg-6 col-12 mb-3">
                  <InputTextPatrocinador
                    label="Nome completo"
                    type="text"
                    requered
                    value={nomeCompleto}
                    onChange={setNomeCompleto}
                    disabled={isPatrocinador}
                  />
                </div>
                <div className="col-lg-6 col-12 mb-3">
                  <InputTextPatrocinador
                    label="Nome para o crachá"
                    type="text"
                    requered
                    value={nomeCracha}
                    onChange={setNomeCracha}
                    disabled={isPatrocinador}
                  />
                </div>
              </div>

              {!isPatrocinador ? (
                <div className="row">
                  <div className="col-12 mb-3">
                    <label
                      className="form-label label-input"
                      htmlFor="perfil-bio"
                    >
                      Bio
                    </label>
                    <textarea
                      id="perfil-bio"
                      className="form-control form-control-lg"
                      rows="4"
                      placeholder="Breve apresentação do participante…"
                      value={bio || ""}
                      onChange={(e) => setBio(e.target.value)}
                    />
                  </div>
                </div>
              ) : null}

              <div className="row">
                {!isPatrocinador ? (
                  <div className="col-lg-6 col-12 mb-3">
                    <InputTextPatrocinador
                      label="CPF"
                      type="text"
                      requered
                      mascara="999.999.999-99"
                      countCPF
                      value={cpf}
                      onChange={setCPF}
                    />
                  </div>
                ) : null}
                <div
                  className={`${isPatrocinador ? "col-12" : "col-lg-6"} col-12 mb-3`}
                >
                  <InputTextPatrocinador
                    label="E-mail"
                    type="email"
                    requered
                    value={email}
                    onChange={() => {}}
                    disabled
                    hint="O e-mail não pode ser alterado por aqui."
                  />
                </div>
              </div>

              {!isPatrocinador ? (
                <>
                  <div className="row">
                    <div className="col-lg-6 col-12 mb-3">
                      <label
                        className="form-label label-input"
                        htmlFor="perfil-dt-nasc"
                      >
                        Data de nascimento{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="perfil-dt-nasc"
                        type="date"
                        className="form-control form-control-lg"
                        value={dataNascimento || ""}
                        onChange={(e) => setDataNascimento(e.target.value)}
                      />
                    </div>
                    <div className="col-lg-6 col-12 mb-3">
                      <InputRadio
                        label="Sexo"
                        values={["Masculino", "Feminino"]}
                        onChange={setSexo}
                        value={sexo}
                        requered
                        validador={() => {}}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6 col-12 mb-3">
                      <InputTextPatrocinador
                        label="Telefone comercial"
                        type="tel"
                        requered
                        mascara="(99) 99999-9999"
                        value={telComercial}
                        onChange={setTelComercial}
                      />
                    </div>
                    <div className="col-lg-6 col-12 mb-3">
                      <InputTextPatrocinador
                        label="Celular"
                        type="tel"
                        requered
                        mascara="(99) 99999-9999"
                        value={celular}
                        onChange={setCelular}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6 col-12 mb-3">
                      <label
                        className="form-label label-input"
                        htmlFor="perfil-estado"
                      >
                        Estado <span className="text-danger">*</span>
                      </label>
                      <select
                        id="perfil-estado"
                        className="form-control form-control-lg"
                        value={estado || ""}
                        onChange={(e) => {
                          const nextEstado = e.target.value;
                          setEstado(nextEstado);
                          setRegiao(regiaoPorEstado(nextEstado) || "");
                        }}
                      >
                        <option value="">Selecione uma opção</option>
                        {ESTADOS_BR.map((nome) => (
                          <option key={nome} value={nome}>
                            {nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-6 col-12 mb-3">
                      <label
                        className="form-label label-input"
                        htmlFor="perfil-regiao"
                      >
                        Região <span className="text-danger">*</span>
                      </label>
                      <select
                        id="perfil-regiao"
                        className="form-control form-control-lg"
                        value={regiao || ""}
                        disabled={!estado}
                      >
                        <option value="">
                          {estado ? "Região" : "Selecione o estado primeiro"}
                        </option>
                        {REGIOES_BR.map((nome) => (
                          <option key={nome} value={nome}>
                            {nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6 col-12 mb-3">
                      <InputTextPatrocinador
                        label="Nome da instituição"
                        type="text"
                        requered
                        value={nomeInstituicao}
                        onChange={setnomeInstituicao}
                        disabled={isParticipantePatrocinador}
                      />
                    </div>
                    <div className="col-lg-6 col-12 mb-3">
                      <InputTextPatrocinador
                        label="Cargo"
                        type="text"
                        requered
                        value={cargo}
                        onChange={setCargo}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="row">
                  <div className="col-12 mb-3">
                    <InputTextPatrocinador
                      label="Celular"
                      type="tel"
                      mascara="(99) 99999-9999"
                      value={celular}
                      onChange={setCelular}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {isConvidado ? (
            <>
              <h3 className="apf-section-title mt-3 mb-3">Dados de credenciamento</h3>
              <p className="form-text text-muted mb-3">
                Campos preenchidos no credenciamento do participante.
              </p>

              <div className="row">
                <div className="col-lg-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="perfil-segmento"
                  >
                    Segmento
                  </label>
                  <select
                    id="perfil-segmento"
                    className="form-control form-control-lg"
                    value={segmento || ""}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSegmento(next);
                      if (next !== "outros") setSegmentoOutros("");
                    }}
                  >
                    {SEGMENTOS.map(([v, lab]) => (
                      <option key={v || "seg-empty"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {segmento === "outros" ? (
                  <div className="col-lg-6 col-12 mb-3">
                    <InputTextPatrocinador
                      label="Descrição do segmento"
                      type="text"
                      value={segmentoOutros}
                      onChange={setSegmentoOutros}
                    />
                  </div>
                ) : null}
                <div className="col-lg-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="perfil-faturamento"
                  >
                    Faturamento estimado 2025
                  </label>
                  <select
                    id="perfil-faturamento"
                    className="form-control form-control-lg"
                    value={faturamento || ""}
                    onChange={(e) => setfaturamento(e.target.value)}
                  >
                    {FATURAMENTO.map(([v, lab]) => (
                      <option key={v || "fat-empty"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <InputTextPatrocinador
                    label="Prioridade de Investimento 26/27"
                    type="text"
                    value={prioridade}
                    onChange={setPrioridade}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="perfil-transformacao"
                  >
                    Qual o maior desafio da sua instituição no momento?
                  </label>
                  <textarea
                    id="perfil-transformacao"
                    className="form-control form-control-lg"
                    rows="3"
                    placeholder="Descreva o principal desafio…"
                    value={transformacao || ""}
                    onChange={(e) => setTransformacao(e.target.value)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="perfil-procura"
                  >
                    O que você procura SBF?
                  </label>
                  <select
                    id="perfil-procura"
                    className="form-control form-control-lg"
                    value={procura || ""}
                    onChange={(e) => {
                      const next = e.target.value;
                      setprocura(next);
                      if (next !== "outros") setOutrosProcura("");
                    }}
                  >
                    {PROCURA.map(([v, lab]) => (
                      <option key={v || "proc-empty"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {procura === "outros" ? (
                  <div className="col-lg-6 col-12 mb-3">
                    <InputTextPatrocinador
                      label="Descrição do que procura"
                      type="text"
                      value={outrosProcura}
                      onChange={setOutrosProcura}
                    />
                  </div>
                ) : null}
              </div>

              <div className="row">
                <div className="col-lg-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="perfil-hobby"
                  >
                    Atividades de Interesse
                  </label>
                  <select
                    id="perfil-hobby"
                    className="form-control form-control-lg"
                    value={hobbyAtividade || ""}
                    onChange={(e) => {
                      const next = e.target.value;
                      atualizarHobby(
                        next,
                        next === "outros" ? hobbyOutros : "",
                      );
                    }}
                  >
                    {ATIVIDADES.map(([v, lab]) => (
                      <option key={v || "hobby-empty"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {hobbyAtividade === "outros" ? (
                  <div className="col-lg-6 col-12 mb-3">
                    <InputTextPatrocinador
                      label="Descrição da atividade"
                      type="text"
                      value={hobbyOutros}
                      onChange={(valor) => atualizarHobby("outros", valor)}
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}

          <h3 className="apf-section-title mt-3 mb-3">Segurança</h3>
          <div className="perfil-seguranca-panel">
            <p className="perfil-seguranca-lead">
              Altere sua senha de acesso informando a senha atual e a nova senha.
              A alteração vale imediatamente após salvar.
            </p>
            <div className="row">
              <div className="col-lg-4 col-12 mb-3">
                <InputTextPatrocinador
                  label="Senha atual"
                  type="password"
                  value={senhaAtual}
                  onChange={setSenhaAtual}
                  placeholder="Digite a senha atual"
                />
              </div>
              <div className="col-lg-4 col-12 mb-3">
                <InputTextPatrocinador
                  label="Nova senha"
                  type="password"
                  value={novaSenha}
                  onChange={setNovaSenha}
                  placeholder="Mínimo de 6 caracteres"
                />
              </div>
              <div className="col-lg-4 col-12 mb-3">
                <InputTextPatrocinador
                  label="Confirmar nova senha"
                  type="password"
                  value={confirmaSenha}
                  onChange={setConfirmaSenha}
                  placeholder="Repita a nova senha"
                />
              </div>
            </div>
            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={handleAlterarSenha}
              disabled={isAlterandoSenha}
            >
              {isAlterandoSenha ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaKey size={14} aria-hidden />
              )}
              {isAlterandoSenha ? "Alterando…" : "Alterar senha"}
            </button>
          </div>

          {formActions}
        </section>
      </div>
    </div>
  );
};

export default TelaPerfil;
